/**
 * @module constants/dashboard-widgets
 * @description 대시보드 위젯 시스템 정적 정의 (SURINP-183 v2 ADDENDUM).
 *
 * 위젯 시스템은 사용자가 위젯의 표시 여부와 순서, 접힘 상태를 LocalStorage에
 * 영속화하여 관리할 수 있게 합니다. 위젯의 권한(adminOnly), 기본 표시 여부,
 * 기본 순서, 레이아웃 위치를 본 모듈에서 단일하게 정의합니다.
 *
 * 사용처:
 * - useDashboardLayout: 사용자별 LocalStorage 상태와 병합하여 visibleWidgets 도출
 * - useDashboardPermission: adminOnly 위젯을 일반 사용자에게서 제거
 * - AdminDashBoardView: 위젯 컴포넌트 동적 렌더링 시 component 이름 매핑
 */

/**
 * 위젯 레이아웃 종류.
 * - 'full': full width (1열)
 * - 'half-left' / 'half-right': 2열 grid 좌측 / 우측 (모바일에서는 자동 1열)
 *
 * @typedef {'full'|'half-left'|'half-right'} WidgetLayout
 */

/**
 * 위젯 정의 객체.
 *
 * @typedef {Object} DashboardWidget
 * @property {string} id - 위젯 고유 식별자 (LocalStorage 키 + DOM data-* 식별자)
 * @property {string} component - Vue 컴포넌트 이름 (AdminDashBoardView의 widgetComponentMap key)
 * @property {boolean} adminOnly - true면 ADMIN 권한 사용자에게만 노출
 * @property {boolean} defaultVisible - 신규 사용자에게 기본 표시 여부 (true=표시)
 * @property {number} defaultOrder - 기본 정렬 순서 (1부터 오름차순). 같은 order는 layout으로 분기
 * @property {WidgetLayout} layout - 그리드 배치 방식
 */

/**
 * 대시보드 위젯 정적 정의 8개.
 *
 * 순서/레이아웃 의도:
 * 1. today-hero (full)         — 헤더 직하 KPI 4메트릭
 * 2. customer-status / activity-feed (half) — 고객사 통계 + 실시간 피드
 * 3. service-quality (full)    — 기존 서비스품질 테이블
 * 4. sla-compliance / region-heatmap (half) — SLA 게이지 + 지역 히트맵 (백엔드 RBAC 필터 적용)
 * 5. partner-kpi (half-left)   — 파트너 KPI (SURINP-209에서 technician-status 위젯 제거 후 단독)
 * 6. technician-calendar (full, ADMIN) — 기사 방문 캘린더 (백엔드 강제 ADMIN-only, 403)
 *
 * 제거 이력: trend-chart — SURINP-289 (리포트 MonthlyTrendChart로 대체) /
 * repair-status — v3 완전 제거 (SURINP-302에서 컴포넌트 파일도 삭제)
 *
 * @type {ReadonlyArray<DashboardWidget>}
 */
export const DASHBOARD_WIDGETS = Object.freeze([
  {
    id: 'today-hero',
    component: 'DashboardHeroPanel',
    adminOnly: false,
    defaultVisible: true,
    defaultOrder: 1,
    layout: 'full'
  },
  {
    id: 'customer-status',
    component: 'CustomerStatusListPanel',
    adminOnly: false,
    defaultVisible: true,
    defaultOrder: 3,
    layout: 'half-left'
  },
  {
    id: 'activity-feed',
    component: 'RealtimeActivityFeedPanel',
    adminOnly: false,
    defaultVisible: true,
    defaultOrder: 3,
    layout: 'half-right'
  },
  {
    id: 'service-quality',
    component: 'ServiceQualityPanel',
    adminOnly: false,
    defaultVisible: true,
    defaultOrder: 4,
    layout: 'full'
  },
  {
    id: 'sla-compliance',
    component: 'SlaCompliancePanel',
    adminOnly: false,
    defaultVisible: true,
    defaultOrder: 5,
    layout: 'half-left'
  },
  {
    id: 'region-heatmap',
    component: 'RegionHeatmap',
    adminOnly: false,
    defaultVisible: true,
    defaultOrder: 5,
    layout: 'half-right'
  },
  {
    id: 'partner-kpi',
    component: 'PartnerKpiTable',
    adminOnly: false,
    defaultVisible: true,
    defaultOrder: 6,
    layout: 'half-left'
  },
  {
    id: 'technician-calendar',
    component: 'TechnicianCalendarPanel',
    adminOnly: true,
    defaultVisible: true,
    defaultOrder: 8,
    layout: 'full'
  }
])

/** 위젯 ID → 위젯 정의 빠른 조회용 인덱스 */
export const WIDGET_BY_ID = Object.freeze(
  DASHBOARD_WIDGETS.reduce((acc, widget) => {
    acc[widget.id] = widget
    return acc
  }, Object.create(null))
)

/**
 * 풀 커스터마이징 패널의 프리셋 정의.
 * 모든 사용자가 선택할 수 있으며, 한 번 선택하면 visibility/order가
 * 일괄 적용되어 LocalStorage에 영속화됩니다.
 * adminOnly 위젯이 visibleIds에 포함되어 있어도 useDashboardPermission이
 * visibleWidgets 단계에서 한 번 더 필터하므로 일반 유저에게는 노출되지 않습니다.
 *
 * - default: 모든 위젯 표시 (defaultVisible/defaultOrder 그대로)
 * - compact: 핵심 KPI만 (today-hero, customer-status, activity-feed, service-quality)
 * - focus: 분석 위주 (today-hero, sla, partner-kpi, region, trend)
 *
 * @type {ReadonlyArray<{id: string, label: string, visibleIds: ReadonlyArray<string>}>}
 */
export const DASHBOARD_PRESETS = Object.freeze([
  {
    id: 'default',
    label: 'dashboard.customize.preset.default',
    visibleIds: Object.freeze(DASHBOARD_WIDGETS.map((w) => w.id))
  },
  {
    id: 'compact',
    label: 'dashboard.customize.preset.compact',
    visibleIds: Object.freeze([
      'today-hero',
      'customer-status',
      'activity-feed',
      'service-quality'
    ])
  },
  {
    id: 'focus',
    label: 'dashboard.customize.preset.focus',
    visibleIds: Object.freeze([
      'today-hero',
      'sla-compliance',
      'partner-kpi',
      'region-heatmap'
    ])
  }
])

/** LocalStorage 스키마 버전. 양식이 깨진 사용자는 default로 폴백된다.
 *  v2: repair-status 위젯 비활성화.
 *  v3: repair-status 위젯 완전 제거 — 기존 사용자 캐시 무효화.
 *  v4: trend-chart 위젯 완전 제거 (SURINP-289) — 기존 사용자 캐시 무효화. */
export const DASHBOARD_LAYOUT_SCHEMA_VERSION = 4

/** LocalStorage 키 prefix. 사용자별로 분리된다 (`${prefix}${userId}`). */
export const DASHBOARD_LAYOUT_STORAGE_PREFIX = 'cs-dashboard-layout:'
